import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import CustomerGrid from '../../shared/customer-grid/customer-grid.component';
import { User } from 'models/user';
import { permissions } from 'utils/permission';
import { hasRequiredPatientNames } from 'components/order-flow/shared/order-input-helper';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { OrderInputType, PatientFormData } from 'models/order-input/shared';
import PatientForm from 'components/order-flow/shared/patient-form.component';
import OrderDentist from 'components/order-flow/shared/order-dentist.component';
import OrderParticipants from 'components/order-flow/shared/order-participants.component';
import { LinkedClinic, LinkedUser } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useLazyQuery } from '@apollo/client';
import { getLabForClinicTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type PatientLabStepProps = {
    onNextClick: () => void;
};
const PatientLabStep: FC<PatientLabStepProps> = ({ onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(SimpleOrderContext);
    const [isPatientFormValid, setIsPatientFormValid] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentUser = currentUserVar();
    const currentOrg = currentOrgVar();
    const isWhiteLabeled = permissions.isWhiteLabeledOrganization(currentOrg);

    const [getLabForClinic] = useLazyQuery(getLabForClinicTemplate, {
        onCompleted({ lab }) {
            setOrder((prev) => ({
                ...prev,
                supplier: lab,
            }));
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        if (isWhiteLabeled && order?.supplier?._id) {
            getLabForClinic({ variables: { id: order.supplier._id } });
        }
    }, [isWhiteLabeled, order?.supplier?._id]);

    const handlePatientChange = (formData: PatientFormData, isFormValid: boolean) => {
        setIsPatientFormValid(isFormValid);
        setOrder((prev) => ({
            ...prev,
            patient: {
                ...prev.patient,
                ...formData,
            },
        }));
    };

    const handleDentistChanged = (dentist: Partial<User>) => {
        setOrder((prev) => ({
            ...prev,
            dentist,
        }));
    };

    const handleParticipantsChange = (
        linkedDentists: LinkedUser[],
        linkedClinics: LinkedClinic[],
        customParticipantEmails: string[],
    ) => {
        setOrder((prev) => ({
            ...prev,
            participants: {
                linkedDentists,
                linkedClinics,
                customParticipantEmails,
            },
        }));
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        {order.inputType === OrderInputType.NEW ? (
                            <Typography variant="h1">{translate('orders:NEW_ORDER')}</Typography>
                        ) : (
                            <Typography variant="h1">{translate('orders:UPDATE_ORDER')}</Typography>
                        )}
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={4}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:PATIENT_INFORMATION')}</Typography>
                        </Box>
                        <PatientForm
                            isEdit={order.inputType !== OrderInputType.NEW}
                            patient={order.patient}
                            onPatientChange={handlePatientChange}
                        />
                    </Box>
                    <Box mb={4}>
                        <Box mb={1}>
                            <Typography variant="h2">{translate('orders:DENTIST_AND_PARTICIPANTS')}</Typography>
                        </Box>
                        <Grid container spacing={3} display="flex" alignItems="stretch">
                            <Grid item xs={12} sm={4}>
                                <OrderDentist currentDentist={order.dentist} onDentistChanged={handleDentistChanged} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <OrderParticipants
                                    participants={order.participants}
                                    onParticipantsChanged={handleParticipantsChange}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    {!order.isWhiteLabeled && permissions.isDentist(currentUser?.type) ? (
                        <Box mb={4}>
                            <Box mb={1}>
                                <Typography variant="h2">{translate('orders:CHOOSE_LAB')}</Typography>
                            </Box>
                            <CustomerGrid
                                isEdit={order.inputType !== OrderInputType.NEW}
                                initialValue={order.supplier}
                            />
                        </Box>
                    ) : undefined}
                    <Box display="flex" justifyContent="center">
                        <Button
                            variant="contained"
                            disabled={
                                !isPatientFormValid || !hasRequiredPatientNames(order.patient) || !order.supplier?._id
                            }
                            onClick={() => onNextClick()}
                            data-test="patient-lab-step.next"
                        >
                            {translate('common:NEXT_STEP')}
                        </Button>
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default PatientLabStep;
